import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { templateKeys } from "@/lib/query";

export interface TemplateItem {
  id: string;
  sectionId: string;
  text: string;
  optional: boolean;
  upload: boolean;
  helpText: string | null;
  order: number;
  createdAt: string;
}

export interface TemplateSection {
  id: string;
  templateId: string;
  title: string;
  order: number;
  createdAt: string;
  items: TemplateItem[];
}

export interface Template {
  id: string;
  name: string;
  description: string | null;
  projectType: string;
  status: string;
  createdById: string;
  createdByName: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface TemplateDetail extends Template {
  sections: TemplateSection[];
}

export function useTemplates(filters?: { status?: string }) {
  return useQuery({
    queryKey: templateKeys.list(filters),
    queryFn: async (): Promise<Template[]> => {
      const params = new URLSearchParams();
      if (filters?.status) params.set("status", filters.status);
      const res = await fetch(`/api/templates?${params}`);
      if (!res.ok) throw new Error("Failed to fetch templates");
      const json = await res.json();
      return json.data;
    },
  });
}

export function useTemplate(id: string | undefined) {
  return useQuery({
    queryKey: id ? templateKeys.detail(id) : (["templates", "detail", null] as const),
    enabled: !!id,
    queryFn: async (): Promise<TemplateDetail> => {
      const res = await fetch(`/api/templates/${id}`);
      if (!res.ok) throw new Error("Failed to fetch template");
      const json = await res.json();
      return json.data;
    },
  });
}

export function useCreateTemplate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: { name: string; description?: string; projectType: string }) => {
      const res = await fetch("/api/templates", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });
      if (!res.ok) throw new Error("Failed to create template");
      const json = await res.json();
      return json.data as Template;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: templateKeys.all });
      toast.success("Template created");
    },
    onError: () => {
      toast.error("Failed to create template");
    },
  });
}

export function useUpdateTemplate(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: {
      name?: string;
      description?: string;
      projectType?: string;
      status?: string;
    }) => {
      const res = await fetch(`/api/templates/${id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });
      if (!res.ok) throw new Error("Failed to update template");
      const json = await res.json();
      return json.data as Template;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: templateKeys.all });
      queryClient.invalidateQueries({ queryKey: templateKeys.detail(id) });
      toast.success("Template updated");
    },
    onError: () => {
      toast.error("Failed to update template");
    },
  });
}

export function useDeleteTemplate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await fetch(`/api/templates/${id}`, { method: "DELETE" });
      if (!res.ok) throw new Error("Failed to delete template");
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: templateKeys.all });
      toast.success("Template deleted");
    },
    onError: () => {
      toast.error("Failed to delete template");
    },
  });
}

export function useCopyTemplate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await fetch(`/api/templates/${id}/copy`, { method: "POST" });
      if (!res.ok) throw new Error("Failed to copy template");
      const json = await res.json();
      return json.data as Template;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: templateKeys.all });
      toast.success("Template copied");
    },
    onError: () => {
      toast.error("Failed to copy template");
    },
  });
}

export function useAddSection(templateId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: { title: string }) => {
      const res = await fetch(`/api/templates/${templateId}/sections`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });
      if (!res.ok) throw new Error("Failed to add section");
      const json = await res.json();
      return json.data as TemplateSection;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: templateKeys.detail(templateId) });
    },
    onError: () => {
      toast.error("Failed to add section");
    },
  });
}

export function useUpdateSection(templateId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ sectionId, title }: { sectionId: string; title: string }) => {
      const res = await fetch(`/api/templates/${templateId}/sections/${sectionId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ title }),
      });
      if (!res.ok) throw new Error("Failed to update section");
      const json = await res.json();
      return json.data as TemplateSection;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: templateKeys.detail(templateId) });
    },
    onError: () => {
      toast.error("Failed to update section");
    },
  });
}

export function useDeleteSection(templateId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (sectionId: string) => {
      const res = await fetch(`/api/templates/${templateId}/sections/${sectionId}`, {
        method: "DELETE",
      });
      if (!res.ok) throw new Error("Failed to delete section");
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: templateKeys.detail(templateId) });
      toast.success("Section deleted");
    },
    onError: () => {
      toast.error("Failed to delete section");
    },
  });
}

export function useAddItem(templateId: string, sectionId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: { text: string; optional?: boolean; upload?: boolean }) => {
      const res = await fetch(`/api/templates/${templateId}/sections/${sectionId}/items`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });
      if (!res.ok) throw new Error("Failed to add item");
      const json = await res.json();
      return json.data as TemplateItem;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: templateKeys.detail(templateId) });
    },
    onError: () => {
      toast.error("Failed to add item");
    },
  });
}

export function useUpdateItem(templateId: string, sectionId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      itemId,
      ...data
    }: {
      itemId: string;
      text?: string;
      optional?: boolean;
      upload?: boolean;
      helpText?: string | null;
    }) => {
      const res = await fetch(
        `/api/templates/${templateId}/sections/${sectionId}/items/${itemId}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(data),
        },
      );
      if (!res.ok) throw new Error("Failed to update item");
      const json = await res.json();
      return json.data as TemplateItem;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: templateKeys.detail(templateId) });
    },
    onError: () => {
      toast.error("Failed to update item");
    },
  });
}

export function useDeleteItem(templateId: string, sectionId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (itemId: string) => {
      const res = await fetch(
        `/api/templates/${templateId}/sections/${sectionId}/items/${itemId}`,
        { method: "DELETE" },
      );
      if (!res.ok) throw new Error("Failed to delete item");
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: templateKeys.detail(templateId) });
      toast.success("Item deleted");
    },
    onError: () => {
      toast.error("Failed to delete item");
    },
  });
}
